import React, { useState, useMemo, useEffect } from 'react';
import { Layout, Menu, Typography, Dropdown, Avatar, Space, Button } from 'antd';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import {
  DashboardOutlined,
  FileOutlined,
  SettingOutlined,
  LogoutOutlined,
  UserOutlined,
  CloudDownloadOutlined,
  AppstoreAddOutlined,
  CloudServerOutlined,
  TeamOutlined,
  MailOutlined, // New icon
  CodeOutlined,
  HomeOutlined,
  TableOutlined,
  FileTextOutlined,
  DownloadOutlined,
  LineChartOutlined,
} from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';
import { getVersionInfo } from '../config/version';
import { fetchMenuItems } from '../utils/api';

const { Header, Content, Sider } = Layout;
const { Title } = Typography;

const iconMap: { [key: string]: React.ReactNode } = {
  DashboardOutlined: <DashboardOutlined />,
  FileOutlined: <FileOutlined />,
  SettingOutlined: <SettingOutlined />,
  LogoutOutlined: <LogoutOutlined />,
  UserOutlined: <UserOutlined />,
  CloudDownloadOutlined: <CloudDownloadOutlined />,
  AppstoreAddOutlined: <AppstoreAddOutlined />,
  CloudServerOutlined: <CloudServerOutlined />,
  TeamOutlined: <TeamOutlined />,
  MailOutlined: <MailOutlined />,
  CodeOutlined: <CodeOutlined />,
  HomeOutlined: <HomeOutlined />,
  TableOutlined: <TableOutlined />,
  FileTextOutlined: <FileTextOutlined />,
  DownloadOutlined: <DownloadOutlined />,
  LineChartOutlined: <LineChartOutlined />,
};

interface MenuItemData {
  label: string;
  path: string;
  icon: string;
}

export const AppLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const versionInfo = getVersionInfo();
  const [menuItems, setMenuItems] = useState<MenuItemData[]>([]);
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    const loadMenuItems = async () => {
      try {
        const response = await fetchMenuItems();
        if (response.ok) {
          const data = await response.json();
          setMenuItems(data);
        } else {
          console.error('Failed to fetch menu items:', response.statusText);
          setMenuItems([]); // Fallback to empty array to prevent crash
        }
      } catch (error) {
        console.error('Failed to fetch menu items:', error);
        setMenuItems([]); // Fallback to empty array on error
      }
    };

    loadMenuItems();
  }, [user]);

  const navItems = useMemo(() => {
    return menuItems.map(item => ({
      key: item.path,
      icon: iconMap[item.icon] || <FileOutlined />,
      label: item.label,
      onClick: () => navigate(item.path),
    }));
  }, [menuItems, navigate]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const userMenuItems = [
    {
      key: 'logout',
      label: 'Logout',
      icon: <LogoutOutlined />,
      onClick: handleLogout,
    },
  ];

  const authLinks = (
    <Space>
        <Button type="primary" onClick={() => navigate('/login')}>Login</Button>
        <Button onClick={() => navigate('/register')}>Register</Button>
    </Space>
  );

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Sider
        collapsible
        collapsed={collapsed}
        onCollapse={setCollapsed}
        width={220}
        style={{
          overflow: 'auto',
          height: '100vh',
          position: 'fixed',
          left: 0,
          top: 0,
          bottom: 0,
        }}
      >
        <div style={{ height: '64px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {!collapsed && (
            <Title level={4} style={{ color: 'white', margin: 0 }}>
              Gudu Portal
            </Title>
          )}
        </div>
        <Menu theme="dark" mode="inline" selectedKeys={[location.pathname]} items={navItems} />
        
        {/* Version Info at bottom of sidebar */}
        <div style={{ 
          position: 'absolute', 
          bottom: '65px', // Adjusted to make space for the toggle button
          width: '100%',
          textAlign: 'center',
          padding: '0 10px',
        }}>
          {!collapsed && (
            <div style={{ 
              color: '#ffffff80', 
              fontSize: '11px',
              lineHeight: '1.4',
              borderTop: '1px solid #404040',
              paddingTop: '12px'
            }}>
              <div>Gudu Portal</div>
              <div>{versionInfo.fullVersion}</div>
              <div style={{ fontSize: '10px', marginTop: '2px' }}>
                {versionInfo.buildDate}
              </div>
            </div>
          )}
        </div>
      </Sider>
      <Layout style={{ marginLeft: collapsed ? 80 : 220, transition: 'margin-left 0.2s' }}>
        <Header style={{ padding: '0 24px', background: '#fff', borderBottom: '1px solid #f0f0f0', display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
          {user ? (
            <Dropdown menu={{ items: userMenuItems }} trigger={['click']}>
              <a onClick={e => e.preventDefault()} style={{cursor: 'pointer'}}>
                <Space>
                  <Avatar icon={<UserOutlined />} size="small" />
                  <span>{user?.first_name || user?.username}</span>
                </Space>
              </a>
            </Dropdown>
          ) : (
            authLinks
          )}
        </Header>
        <Content style={{ margin: '24px 16px 0', overflow: 'initial' }}>
          <div style={{ padding: 24, background: '#fff', minHeight: '85vh' }}>
            {children}
          </div>
        </Content>
      </Layout>
    </Layout>
  );
}; 